import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import styles from './EditContent.module.scss';
import { useEditCourse } from '../useEditCourse';
import Button from '../../../../../components/button/Button';
import { TextInput, TextInputValue } from '../../../../../components/text-input/TextInput';
import { Content, ContentType } from '../../../../../types';
import { uploadFile } from '../../../../../services/File';
import { isEmptyObject, validateIsEmpty } from '../../../../../utils/validation';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { UpChunk } from "@mux/upchunk";
import { useQueryDictionary } from '../../../../../hooks/useQueryDictionary';
import SelectMulti from '../../../../../components/select-multi/SelectMulti';
import ContentAudio from '../../../../../components/content-audio/ContentAudio';
import ContentPDF from '../../../../../components/content-pdf/ContentPDF';
import ContentSlide from '../../../../../components/content-slide/ContentSlide';
// import Checkbox from '../../../../../components/checkbox/Checkbox';
import FeedbackFormEditor from './feedback-form-editor/FeedbackFormEditor';
import { FeedbackFormProvider, useFeedbackForm } from './feedback-form-editor/useFeedbackFormEdit';
import VideoForm from './video-form/VideoForm';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../../utils/logger';

type Props = {};

const emptyErrors = {
  name: '',
  position: '',
  feedbackForm: '',
};

type ErrorKeys = keyof typeof emptyErrors;

const updateContentMutation = gql`
    mutation UpdateContent($input: UpdateContentInput!) {
        updateContent(input: $input) {
            id, name, type, description, position, data, pauseHere, formData
        }
    }
`;

type UpdateContentResponse = {
  updateContent: Content;
};

const urlQuery = gql`
    query GetUrl {
        videoUpload {
            uploadId
            url
        }
    }
`;

type UrlQueryResponse = {
  videoUpload: {
    uploadId: string;
    url: string;
  };
};

const removeContentMutation = gql`
    mutation RemoveContent($input: IDInput!) {
        removeContent(input: $input)
    }
`;

type RemoveContentResponse = {
  removeContent: boolean;
};

const updateVideoMutation = gql`
    mutation UpdateVideo($input: UpdateVideoContentInput!) {
        updateVideoContent(input: $input) {
            id, name, type, description, position, data, pauseHere
        }
    }
`;

type UpdateVideoMutation = {
  updateVideoContent: Content;
};

const queryContentGroups = gql`
    query ContentGroups($input: IDInput!) {
        contentGroups(input: $input)
    }
`;

type ContentGroupsResponse = {
  contentGroups: string[];
};

type UploadCallback = (params: { data: string, uploadId: string } | undefined) => void
// const MuxDomain = 'https://stream.mux.com';

function EditContentComponent(props: Props) {
  const { content, lesson, setSelectionLevel, setContent } = useEditCourse();
  const { awsKeys } = useAWSKeys();

  const [getUrl, { loading: urlLoading, data: videoUrlUploadData }] = useLazyQuery<UrlQueryResponse>(urlQuery, {
    fetchPolicy: 'network-only',
  });

  const {
    data: contentGroupsData,
    refetch: refetchContentGroups
  } = useQuery<ContentGroupsResponse>(queryContentGroups, { variables: { input: { id: content!.id } } });
  const groupOptions = useQueryDictionary({ dictionary: 'ProgramTypes' });

  const [removeContent] = useMutation<RemoveContentResponse>(removeContentMutation, {
    update(cache, res) {
      setLoading(false);
      if (res.data?.removeContent) {
        cache.modify({
          id: cache.identify(lesson!),
          fields: {
            teacherPreparation(existingRefs, { readField }) {
              return existingRefs.filter((ref: any) => content!.id !== readField('id', ref));
            },
            classPresentation(existingRefs, { readField }) {
              return existingRefs.filter((ref: any) => content!.id !== readField('id', ref));
            }
          }
        });
      }
    },
    onCompleted() {
      setSelectionLevel('Lesson');
      setContent(undefined);
    },
    onError(e) {
      setLoading(false);
      alert('Something went wrong.');
    },
  });

  const [updateVideoContent] = useMutation<UpdateVideoMutation>(updateVideoMutation, {
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
      setLoading(false);
    },
    update(cache, res) {
      const d = res?.data?.updateVideoContent;

      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: {
            ...d,
          },
          fragment: gql`
              fragment UpdateContent on Content {
                  id, name, description, position, data
              }
          `,
        });
        updateLocalContent(d);
        refetchContentGroups();
      }
      clearFile();
      setLoading(false);
    }
  });

  const [updateContent] = useMutation<UpdateContentResponse>(updateContentMutation, {
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
      setLoading(false);
    },
    update(cache, res) {
      const d = res?.data?.updateContent;

      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: {
            ...d,
          },
          fragment: gql`
              fragment UpdateContent on Content {
                  id, name, description, position, data, formData
              }
          `,
        });
        updateLocalContent(d);
        refetchContentGroups();
        clearFile();
      }
      setLoading(false);
    }
  });

  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [name, setName] = useState(content?.name ?? '');
  const [description, setDescription] = useState(content?.description ?? '');
  const [position, setPosition] = useState(content?.position ?? 1);
  const [data, setData] = useState(content?.data ?? '');
  const [formData, setFormData] = useState<string>(content?.formData ?? '');
  const [fileUploadingPercent, setFileUploadingPercent] = useState(0);
  const [groups, setGroups] = useState<string[]>([]);
  const [pauseHere, setPauseHere] = useState<boolean>(false);

  const [dataFile, setDataFile] = useState<File | undefined>();
  const dataInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<Partial<Record<ErrorKeys, string>>>(emptyErrors);

  const { getFormState, hasError: hasFeedbackFormErrors } = useFeedbackForm();

  useEffect(() => {
    setGroups(contentGroupsData?.contentGroups ?? []);
  }, [contentGroupsData?.contentGroups]);

  useEffect(() => {
    updateLocalContent(content);
  }, [content]);

  const updateLocalContent = (content: Content | undefined) => {
    setName(content?.name ?? '');
    setDescription(content?.description ?? '');
    setPosition(content?.position ?? 1);
    setData(content?.data ?? '');
    setFormData(content?.formData ?? '');
    setFileUploadingPercent(0);
    setPauseHere(content?.pauseHere ?? false);
  }

  const clearFile = () => {
    setDataFile(undefined);
    if (dataInputRef.current) {
      dataInputRef.current.value = '';
    }
    setFileUploadingPercent(0);
  }

  useEffect(() => {
    clearFile();
  }, [content?.id]);

  const validate = useCallback(() => {
    const errors: Partial<Record<ErrorKeys, string>> = {};

    if (validateIsEmpty(name)) {
      errors.name = 'Name is required.';
    }

    if (validateIsEmpty(position)) {
      errors.position = 'Position is required.';
    }

    return errors;
  }, [name, position]);

  const handleSave = () => {
    if (loading || fileLoading) return;

    const errors = validate();

    if (content!.type === ContentType.FORM) {
      if (hasFeedbackFormErrors) {
        errors.feedbackForm = 'Feed feedback form'
      }
    }

    if (isEmptyObject(errors)) {
      setLoading(true);

      if (content!.type === ContentType.VIDEO) {
        if (dataFile) {
          handleUploadVideo((res) => {
            if (res) {
              updateVideoContent({
                variables: {
                  input: {
                    id: content!.id,
                    type: content!.type,
                    name,
                    description,
                    position: +position || 1,
                    groups: groups ?? [],
                    pauseHere: pauseHere,
                    uploadId: res.uploadId,
                    data: res.data,
                  }
                }
              });
            } else {
              updateVideoContent({
                variables: {
                  input: {
                    id: content!.id,
                    type: content!.type,
                    name,
                    description,
                    position: +position || 1,
                    groups: groups ?? [],
                    pauseHere: pauseHere,
                    data,
                  }
                }
              });
            }
          })
        } else {
          updateVideoContent({
            variables: {
              input: {
                id: content!.id,
                type: content!.type,
                name,
                description,
              position: +position || 1,
                uploadId: videoUrlUploadData?.videoUpload.uploadId,
                groups: groups ?? [],
                pauseHere: pauseHere,
                data,
              }
            }
          });
        }
      } else if (content!.type === ContentType.PARENT) {
        updateContent({
          variables: {
            input: {
              id: content!.id,
              type: content!.type,
              name,
              position: +position || 1,
              groups: [],
            }
          }
        });
      } else {
        let formData = '';
        if (content!.type === ContentType.FORM) {
          formData = JSON.stringify(getFormState());
        }
        updateContent({
          variables: {
            input: {
              id: content!.id,
              type: content!.type,
              name,
              description,
              position: +position || 1,
              data,
              groups: groups ?? [],
              pauseHere: pauseHere,
              formData
            }
          }
        });
      }
    } else {
      setErrors(errors);
    }
  };

  const handleChangeText = useCallback((setter, key?: ErrorKeys) => (value: TextInputValue) => {
    setter(value);
    if (key) {
      setErrors({
        ...errors,
        [key]: '',
      });
    }
  }, [errors]);

  const handleSetSlideFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    setDataFile(file);
  }, []);

  const handleUploadSlide = useCallback(async () => {
    if (dataFile) {
      try {
        setLoading(true);
        const location = await uploadFile(awsKeys, dataFile, 'content', (percent) => {
          setFileUploadingPercent(percent);
        });
        setData(location);
      } catch (e) {
        logger.error('Error uploading file:', e);
        const errorMessage = e instanceof Error ? e.message : 'Unknown error';
        alert(`Upload failed: ${errorMessage}. Please try again or contact support.`);
        clearFile();
      } finally {
        setLoading(false);
      }
    }
  }, [dataFile, clearFile]);

  const handleSetVideoFile = useCallback((file) => {
    if (file) {
      getUrl();
      setDataFile(file);
    } else {
      clearFile();
    }
  }, [getUrl]);

  const handleUploadVideo = (cb: UploadCallback) => {
    if (dataFile && videoUrlUploadData?.videoUpload.url && videoUrlUploadData?.videoUpload.uploadId) {
      setFileLoading(true);

      const upload = UpChunk.createUpload({
        endpoint: videoUrlUploadData.videoUpload.url,
        file: dataFile,
      });

      upload.on('error', err => {
        setFileLoading(false);
        clearFile();
        window.alert('Some error occurred.');
        cb(undefined);
      });

      upload.on('progress', (progress, ...rest) => {
        setFileUploadingPercent(Math.floor(progress.detail));
      });

      upload.on('success', () => {
        setData(videoUrlUploadData.videoUpload.url);
        setFileLoading(false);
        clearFile();
        cb({
          data: videoUrlUploadData.videoUpload.url,
          uploadId: videoUrlUploadData.videoUpload.uploadId
        });
      });
    }
  };

  const contentType = content?.type;

  const genericContent = () => {
    switch (contentType) {
      case ContentType.PDF: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input type="file" accept={'application/pdf'} onChange={handleSetSlideFile} ref={dataInputRef}/>
              <Button title={!dataFile ? "Please add file to left prior to uploading": ""} disabled={!dataFile || loading} label={loading ? `Uploading ${fileUploadingPercent}%` : 'Upload'}
                      size={'extraSmall'} onClick={handleUploadSlide}/>
            </div>
            {data &&
            <div className={`${styles.row} ${styles.preview}`}>
              <ContentPDF data={data}/>
            </div>
            }
          </>
        );
      }
      case ContentType.SLIDE: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input type="file" accept={'application/pdf'} onChange={handleSetSlideFile} ref={dataInputRef}/>
              <Button title={!dataFile ? "Please add file to left prior to uploading": ""} disabled={!dataFile || loading} label={loading ? `Uploading ${fileUploadingPercent}%` : 'Upload'}
                      size={'extraSmall'} onClick={handleUploadSlide}/>
            </div>
            {data &&
            <div className={`${styles.row} ${styles.preview}`}>
              <ContentSlide data={data}/>
            </div>
            }
          </>
        );
      }
      case ContentType.SKILL: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input type="file" accept={'audio/*'} onChange={handleSetSlideFile} ref={dataInputRef}/>
              <Button title={!dataFile ? "Please add file to left prior to uploading": ""} disabled={!dataFile || loading} label={loading ? `Uploading ${fileUploadingPercent}%` : 'Upload'}
                      size={'extraSmall'} onClick={handleUploadSlide}/>
            </div>
            {data &&
            <div className={`${styles.row} ${styles.preview}`}>
              <ContentAudio data={data}/>
            </div>
            }
          </>
        );
      }
      case ContentType.AUDIO: {
        return (
          <>
            <div className={`${styles.row} ${styles.rowBottom}`}>
              <input type="file" accept={'audio/*'} onChange={handleSetSlideFile} ref={dataInputRef}/>
              <Button title={!dataFile ? "Please add file to left prior to uploading": ""} disabled={!dataFile || loading} label={loading ? `Uploading ${fileUploadingPercent}%` : 'Upload'}
                      size={'extraSmall'} onClick={handleUploadSlide}/>
            </div>
            {data &&
            <div className={`${styles.row} ${styles.preview}`}>
              <ContentAudio data={data}/>
            </div>
            }
          </>
        );
      }
      case ContentType.VIDEO: {
        return (
          <VideoForm
            data={data}
            meta={content!.meta}
            dataFile={dataFile}
            onChange={handleSetVideoFile}
            isUploading={fileLoading}
            uploadingPercent={fileUploadingPercent}
          />
        );
      }
      case ContentType.FORM: {
        return (
          <FeedbackFormEditor showError={!!errors.feedbackForm} formData={formData}/>
        );
      }
      default: {
        return null;
      }
    }
  };

  const handleRemove = () => {
    if (window.confirm('Are you sure you want to remove the content?')) {
      setLoading(true);
      removeContent({
        variables: {
          input: { id: content!.id },
        }
      });
    }
  };

  const handleChangeGroups = useCallback((v) => {
    setGroups(v);
  }, []);

  // const handleChangePauseHere = useCallback((v) => {
  //   setPauseHere(v);
  // }, []);

  // const isFormType = content && content.type !== ContentType.FORM;
  const isParent = content && content.type === ContentType.PARENT;
  const isSkill = content && content.type === ContentType.SKILL;
  return (
    <>
      <div className={styles.row}>
        <div className={styles.actions}>
          <div className={styles.title}>
            Edit content
          </div>
          <div className={styles.buttons}>
            <Button disabled={loading || urlLoading || fileLoading} onClick={handleSave} size={'small'} label={'SAVE'}/>
            <Button color={'red'} disabled={loading} onClick={handleRemove} outline={true} size={'small'}
                    label={'DELETE'}/>
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <TextInput hasError={!!errors.name} subTitle={errors.name} label={'Name'} id={'name'} placeholder="Name" value={name === "Content name" ? "" : name} readonly={isParent}
                   onChange={handleChangeText(setName, 'name')}/>
      </div>
      {isSkill &&
        <div className={styles.row}>
          <TextInput textArea={true} label={'Description'} value={description === "Content description" ? "" : description} id={'description'} placeholder="Description" onChange={handleChangeText(setDescription)}/>
        </div>}
      <div className={styles.row}>
        <TextInput hasError={!!errors.position} subTitle={errors.position} type={'number'} label={'Position'}
                   id={'position'} value={position} onChange={handleChangeText(setPosition, 'position')}/>
      </div>
      {!isParent && !isSkill &&
      <div className={styles.row}>
        <SelectMulti label={'Groups'} options={groupOptions} onChange={handleChangeGroups} value={groups}/>
      </div>
      }
      {/* {isFormType && !isParent && !isSkill &&
      <div className={styles.row}>
        <Checkbox id={'pauseHere'} checked={pauseHere} onChange={handleChangePauseHere} label={'Pause here'}/>
      </div>
      } */}
      {genericContent()}
    </>
  );
}

const EditContent = () => {
  return (
    <FeedbackFormProvider>
      <EditContentComponent/>
    </FeedbackFormProvider>
  );
}

export default EditContent;
